import client from '../graphql/client';
import { Auth } from 'aws-amplify';
import Cache from '@aws-amplify/cache';
import Honeybadger from '@honeybadger-io/js';

export const getCognitoUserId: () => Promise<null | string> = async () => {
  try {
    const cognitoUser = await Auth.currentAuthenticatedUser();
    return cognitoUser.attributes ? cognitoUser.attributes.sub : cognitoUser.id;
  } catch (e) {
    return Promise.resolve(null);
  }
};

export const getAWSIdToken: () => Promise<null | string> = async () => {
  const cognitoUser = await Auth.currentAuthenticatedUser();
  return cognitoUser.signInUserSession.idToken.jwtToken;
};

export const getGoogleIdToken: () => Promise<null | string> = async () => {
  const token = await Cache.getItem('@GoogleSSO:idToken');
  return token;
};

export const getEpicSsoIdToken: () => null | string = () => {
  // when used in mobile app we don't have access to sessionStorage
  if (!window || !window.sessionStorage) return null;

  const token = window.sessionStorage.getItem('epicSsoIdToken');
  return token;
};

export const getAppleSsoIdToken: () => Promise<null | string> = async () => {
  const token = await Cache.getItem('@AppleSSO:idToken');
  return token;
};

export const waitUntilEpicSsoIdTokenIsSet: () => Promise<null> = async () => {
  return new Promise(function(resolve) {
    (function waitForEpic() {
      if (getEpicSsoIdToken()) return resolve();
      setTimeout(waitForEpic, 1000);
    })();
  });
};

export const getMedtronicSsoIdToken: () => null | string = () => {
  // when used in mobile app we don't have access to sessionStorage
  if (!window || !window.sessionStorage) return null;

  const token = window.sessionStorage.getItem('medtronicSsoIdToken');
  return token;
};

export const waitUntilMedtronicSsoIdTokenIsSet: () => Promise<null> = async () => {
  return new Promise(function(resolve) {
    (function waitForMedtronic() {
      if (getMedtronicSsoIdToken()) return resolve();
      setTimeout(waitForMedtronic, 1000);
    })();
  });
};

export const getIdToken: () => Promise<null | string> = async () => {
  let idToken = getEpicSsoIdToken();
  if (idToken) return idToken;

  idToken = getMedtronicSsoIdToken();
  if (idToken) return idToken;

  idToken = await getAppleSsoIdToken();
  if (idToken) return idToken;

  idToken = await getGoogleIdToken();
  if (idToken) return idToken;

  // the registration and login workflows are very delicate -- you cannot set idtoken too early
  // during registration because you would confuse the existing code and make it think
  // you already finished the registration (this for example leads to empty list of labs in registration form via google SSO)

  // we don't know exactly how the registration workflow goes, but we can rely that the cognitoid
  // was set exactly at the "right time" (and not sooner)
  // and so here, we just look if cognitoid is set, and if yes, we actually set idtoken

  // Epic SSO will not use the standard registration at all, so for Epic will set here idtoken
  // as soon as we will have it

  const cognitoId = await getCognitoUserId();
  if (!cognitoId) return null;

  idToken = await getAWSIdToken();
  return idToken;
};

export const isEpicUser = () => {
  return !!getEpicSsoIdToken();
};

export const isMedtronicUser = () => {
  return !!getMedtronicSsoIdToken();
};

export const logout = async () => {
  // Only reset the apollo store if there is an existing cognito id
  const existingCognitoId = await getCognitoUserId();
  if (existingCognitoId) {
    await client.clearStore();
  }

  await Cache.removeItem('@AppleSSO:idToken');
  await Cache.removeItem('@GoogleSSO:idToken');

  if (!window || !window.sessionStorage) return;

  window.sessionStorage.removeItem('epicSsoIdToken');
  window.sessionStorage.removeItem('medtronicSsoIdToken');
  window.sessionStorage.removeItem('appleSsoIdToken');
};
